import { useState, useCallback } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/lilia-ui/select";
import { BlockEditModal } from "../BlockEditModal";
import { sanitizeToHtml } from "@/components/preview/BlockRenderer";
import styles from "../blocks/blocks.module.scss";

type FrontMatterSubType = "titlePage" | "copyright" | "dedication" | "acknowledgments" | "preface" | "foreword" | "epigraph";

const FRONT_MATTER_TYPES: FrontMatterSubType[] = [
  "titlePage", "copyright", "dedication", "acknowledgments", "preface", "foreword", "epigraph",
];

const FRONT_MATTER_LABELS: Record<FrontMatterSubType, string> = {
  titlePage: "Title Page",
  copyright: "Copyright",
  dedication: "Dedication",
  acknowledgments: "Acknowledgments",
  preface: "Preface",
  foreword: "Foreword",
  epigraph: "Epigraph",
};

const CENTERED_TYPES: FrontMatterSubType[] = ["titlePage", "dedication", "epigraph"];

export function FrontMatterNodeView({ node, updateAttributes, selected, deleteNode }: NodeViewProps) {
  const rawSubType = node.attrs.subType as string;
  const subType: FrontMatterSubType = FRONT_MATTER_TYPES.includes(rawSubType as FrontMatterSubType)
    ? (rawSubType as FrontMatterSubType)
    : "titlePage";
  const text = (node.attrs.text as string) || "";

  const isCentered = CENTERED_TYPES.includes(subType);

  const [isOpen, setIsOpen] = useState(false);
  const [draftSubType, setDraftSubType] = useState<FrontMatterSubType>(subType);
  const [draftText, setDraftText] = useState(text);

  const handleOpen = useCallback(() => {
    setDraftSubType(subType);
    setDraftText(text);
    setIsOpen(true);
  }, [subType, text]);

  const handleSave = useCallback(() => {
    updateAttributes({
      subType: draftSubType,
      text: draftText,
    });
    setIsOpen(false);
  }, [draftSubType, draftText, updateAttributes]);

  return (
    <NodeViewWrapper data-type="front-matter-block" data-block-id={node.attrs.blockId} className={selected ? "ProseMirror-selectednode" : ""}>
      <div
        className={styles.frontMatterBlock}
        onClick={handleOpen}
        style={{ cursor: "pointer", textAlign: isCentered ? "center" : "left" }}
        title="Click to edit front matter"
      >
        <div className={styles.frontMatterLabel}>{FRONT_MATTER_LABELS[subType]}</div>
        {text ? (
          <div
            className={styles.frontMatterContent}
            dangerouslySetInnerHTML={{ __html: sanitizeToHtml(text) }}
          />
        ) : (
          <div className={styles.frontMatterContent}>
            <span style={{ color: "var(--lilia-text-muted, #999)" }}>Click to add content...</span>
          </div>
        )}
      </div>

      <BlockEditModal
        sourceNode={node}
        title={`Edit ${FRONT_MATTER_LABELS[draftSubType]}`}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSave={handleSave}
        onDelete={() => { setIsOpen(false); deleteNode(); }}
        width={640}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Type</label>
          <Select value={draftSubType} onValueChange={(val) => setDraftSubType((val || "titlePage") as FrontMatterSubType)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FRONT_MATTER_TYPES.map((type) => (
                <SelectItem key={type} value={type}>{FRONT_MATTER_LABELS[type]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Content</label>
          <textarea
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
            placeholder={`Enter ${FRONT_MATTER_LABELS[draftSubType].toLowerCase()} content...`}
            rows={8}
            autoFocus
            style={{
              width: "100%", padding: "12px", fontSize: 13,
              border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4,
              resize: "vertical", lineHeight: 1.6,
            }}
          />
        </div>
      </BlockEditModal>
    </NodeViewWrapper>
  );
}
